import { type YDatabase } from '@databases/collab/y-database';
import { type EntrySdkContract } from '@databases/sdk-types';
import { err, ok, type Result } from 'neverthrow';

import {
  addEntrySchema,
  removeEntrySchema,
  reorderEntriesSchema,
  type AddEntryInput,
  type RemoveEntryInput,
  type ReorderEntriesInput
} from '../domain/entries/schemas';
import { assertEntryExists } from '../domain/guards/assert-entry-exists';
import { assertEntryNotExists } from '../domain/guards/assert-entry-not-exists';
import { assertEntryValueTypesSupported } from '../domain/guards/assert-entry-value-types-supported';
import { assertIndexInRange } from '../domain/guards/assert-index-in-range';
import { parse, tryMutate } from '../domain/result';
import { addEntry } from '../handlers/entry/add-entry';
import { deleteEntry } from '../handlers/entry/delete-entry';
import { reorderEntries } from '../handlers/entry/reorder-entries';
import { type DatabaseEntry, type SdkError } from '../types';

/**
 * Public API for reading and mutating database entries (rows).
 *
 * Every mutation runs a three-step pipeline:
 *   1. **Zod validation** — rejects malformed input → `VALIDATION_ERROR`
 *   2. **Business rules** — rejects invalid document state → `STATE_ERROR`
 *      (ENTRY_NOT_FOUND, ENTRY_ALREADY_EXISTS, INDEX_OUT_OF_RANGE)
 *   3. **Handler mutation** — delegates to collab; only truly unexpected
 *      throws surface as `INTERNAL_ERROR`
 *
 * Entries are **not** gated by `isLocked` — content edits stay allowed
 * when the database structure is locked.
 */
export class EntrySdk implements EntrySdkContract {
  constructor(private readonly ydb: YDatabase) {}

  add(input: AddEntryInput): Result<void, SdkError> {
    return parse(addEntrySchema, input).andThen(({ entry, index }) =>
      assertEntryNotExists(this.ydb, entry.id)
        .andThen(() => assertEntryValueTypesSupported(entry.values))
        .andThen(() =>
          index != null
            ? assertIndexInRange(index, this.ydb.entries.length)
            : ok(undefined)
        )
        .andThen(() =>
          tryMutate(() => addEntry(this.ydb, { entry: entry as DatabaseEntry, index }))
        )
    );
  }

  remove(input: RemoveEntryInput): Result<void, SdkError> {
    return parse(removeEntrySchema, input).andThen(({ entryId }) =>
      assertEntryExists(this.ydb, entryId).andThen(() =>
        tryMutate(() => deleteEntry(this.ydb, entryId))
      )
    );
  }

  reorder(input: ReorderEntriesInput): Result<void, SdkError> {
    return parse(reorderEntriesSchema, input).andThen(({ entryIds, newIndex }) => {
      for (const id of entryIds) {
        const check = assertEntryExists(this.ydb, id);

        if (check.isErr()) {
          return err(check.error);
        }
      }

      return assertIndexInRange(
        newIndex,
        this.ydb.entries.length - entryIds.length
      ).andThen(() => tryMutate(() => reorderEntries(this.ydb, { entryIds, newIndex })));
    });
  }
}
